// OWNER: FRONTEND-DIALER agent. Sign-in (SPEC §3, AUTH_MODE=password) and first-admin setup (bootstrap token).
import '../dialer/dialer.css';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { Me } from '../../../src/shared/api';
import { api, ApiFailure, errorMessage } from '../api';
import { ErrorBanner } from '../components/ui';
import { Field } from '../components/ui-extra';
import { phone } from '../phone/manager';
import { usePhone } from '../phone/PhoneProvider';

function explainLoginError(e: unknown): string {
  if (e instanceof ApiFailure) {
    if (e.status === 401) return e.body.message || 'Wrong email or password.';
    return e.body.message || `Sign-in failed (${e.status}).`;
  }
  if (e instanceof TypeError) return "Can't reach the dialer server. Check your internet connection.";
  return errorMessage(e);
}

async function signIn(email: string, password: string): Promise<Me> {
  await api.post('/api/auth/login', { email, password });
  return api.get<Me>('/api/auth/me');
}

export function Login(props: { onSignedIn: (me: Me) => void }) {
  const [mode, setMode] = useState<'signin' | 'setup'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [token, setToken] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const p = usePhone();
  useEffect(() => { emailRef.current?.focus(); }, [mode]);

  async function submit(e: Event) {
    e.preventDefault();
    if (busy) return;
    setError(null);
    const em = email.trim();
    if (!em || !password) { setError('Enter your email and password.'); return; }
    if (mode === 'setup') {
      if (!token.trim() || !name.trim()) { setError('Enter the setup token and your name.'); return; }
      if (password !== confirm) { setError("The two passwords don't match."); return; }
    }
    setBusy(true);
    try {
      if (mode === 'setup') await api.post('/api/auth/bootstrap', { token: token.trim(), email: em, name: name.trim(), password });
      props.onSignedIn(await signIn(em, password));
    } catch (err) {
      setError(explainLoginError(err));
      setPassword('');
      setConfirm('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="vd-login">
      <div class="card">
        <div class="vd-login-brand"><img src="/brand/valistix-mark-color.svg" alt="" width="30" height="30" /><div>Valistix<span>.</span> Dialer</div></div>
        <div class="muted">{mode === 'signin' ? 'Sign in to start your block.' : 'First-time setup: create the first admin account.'}</div>

        {p.active ? (
          <div class="chip chip-warn" style="display:flex;justify-content:space-between;gap:8px;padding:8px 10px;margin-top:12px;font-size:13px">
            <span>You're still on a call.</span>
            <button class="btn btn-sm btn-danger" onClick={() => phone.hangup()}>Hang up</button>
          </div>
        ) : null}

        <form onSubmit={submit} autocomplete="on">
          {mode === 'setup' ? (
            <>
              <Field label="Setup token" hint="The BOOTSTRAP_TOKEN secret. It only works while no users exist.">
                <input type="password" value={token} onInput={(e) => setToken(e.currentTarget.value)} autocomplete="off" />
              </Field>
              <Field label="Your name"><input value={name} onInput={(e) => setName(e.currentTarget.value)} autocomplete="name" /></Field>
            </>
          ) : null}
          <Field label="Email">
            <input ref={emailRef} type="email" value={email} onInput={(e) => setEmail(e.currentTarget.value)} autocomplete="username" />
          </Field>
          <Field label="Password">
            <input type="password" value={password} onInput={(e) => setPassword(e.currentTarget.value)} autocomplete={mode === 'setup' ? 'new-password' : 'current-password'} />
          </Field>
          {mode === 'setup' ? (
            <Field label="Password again">
              <input type="password" value={confirm} onInput={(e) => setConfirm(e.currentTarget.value)} autocomplete="new-password" />
            </Field>
          ) : null}
          <ErrorBanner error={error} />
          <button type="submit" class="btn btn-primary" disabled={busy}>{busy ? 'One moment…' : mode === 'signin' ? 'Sign in' : 'Create admin and sign in'}</button>
        </form>

        <div style="margin-top:14px;font-size:13px">
          {mode === 'signin'
            ? <a href="#" onClick={(e) => { e.preventDefault(); setMode('setup'); setError(null); }}>First-time setup</a>
            : <a href="#" onClick={(e) => { e.preventDefault(); setMode('signin'); setError(null); }}>Back to sign in</a>}
          <p class="muted" style="margin:8px 0 0">Forgot your password? Ask an admin to reset it in Settings.</p>
        </div>
      </div>
    </div>
  );
}
